/**
 * The chart's rendered SVG, for the PDF export.
 *
 * Taken from the DOM rather than rebuilt so the PDF matches what the user is looking at,
 * including whatever they toggled off in the legend. Highcharts renders one SVG per chart
 * container; the explorer shows one chart at a time.
 */
export const captureChartSvg = (container: HTMLElement | null): string | null => {
  const svg = container?.querySelector(".highcharts-container svg");
  if (!svg) {
    return null;
  }
  const clone = svg.cloneNode(true) as SVGElement;
  resolveCssVariableColors(svg, clone);
  // A detached SVG carries no stylesheet, so the namespace has to be explicit for it to
  // parse as SVG on the server.
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  return new XMLSerializer().serializeToString(clone);
};

/**
 * Highcharts 13 paints with `var(--highcharts-…)`, defined in CSS that does not travel with
 * the markup (and that the server strips anyway). The server renderer cannot resolve the
 * variables and paints every such shape black, so the colors the browser already resolved
 * are written onto the clone as plain attributes.
 */
const resolveCssVariableColors = (live: Element, clone: Element) => {
  const liveElements = [live, ...live.querySelectorAll("*")];
  const cloneElements = [clone, ...clone.querySelectorAll("*")];
  liveElements.forEach((element, index) => {
    const target = cloneElements[index];
    const inline = (element as SVGElement).style;
    let computed: CSSStyleDeclaration | null = null;
    for (const property of ["fill", "stroke", "stop-color"]) {
      const declared = `${element.getAttribute(property) ?? ""} ${inline?.getPropertyValue(property) ?? ""}`;
      if (!declared.includes("var(")) {
        continue;
      }
      computed ??= getComputedStyle(element);
      const resolved = computed.getPropertyValue(property);
      if (resolved && !resolved.includes("var(")) {
        target.setAttribute(property, resolved);
      }
    }
  });
};
